import { Controller } from '@hotwired/stimulus';

// The marker: draw a region on a frame, name it, keep it. Marks are stored
// in the frame's own 0..1 coordinates (app/models/mark.rb); the canvas is
// only a drawing of them at whatever size the picture renders at, so a
// resize is a redraw and never a rewrite. Drag to draw, a number key picks
// the label, Enter saves, Esc drops, Delete removes the selected one; arrows
// nudge it (shift: 5x) and a shift-drag pulls its bottom-right corner.
export default class extends Controller {
  static targets = ['stage', 'image', 'canvas', 'form', 'label', 'value', 'list', 'count'];
  static values = { url: String, marks: Array, labels: Array, read: Boolean, remove: String };

  connect() {
    this.marks = this.marksValue;
    this.selected = null;
    this.draft = null;
    this.onKey = (e) => this.key(e);
    document.addEventListener('keydown', this.onKey);
    this.resize = new ResizeObserver(() => this.fit());
    this.resize.observe(this.imageTarget);
    if (this.imageTarget.complete) this.fit(); else this.imageTarget.onload = () => this.fit();
  }

  disconnect() {
    document.removeEventListener('keydown', this.onKey);
    this.resize?.disconnect();
  }

  fit() {
    const c = this.canvasTarget;
    c.width = this.imageTarget.clientWidth;
    c.height = this.imageTarget.clientHeight;
    this.draw();
  }

  // ---- pointer ----
  at(e) {
    const r = this.canvasTarget.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  }

  down(e) {
    if (!this.formTarget.hidden) return;
    const p = this.at(e);
    const hit = this.marks.findLast((m) => p.x >= m.x && p.x <= m.x + m.w && p.y >= m.y && p.y <= m.y + m.h);
    if (hit) {
      this.selected = hit;
      if (e.shiftKey) {
        this.sizing = { p, w: hit.w, h: hit.h };
        this.canvasTarget.setPointerCapture(e.pointerId);
      }
      this.draw();
      return;
    }
    this.selected = null;
    this.draft = { x: p.x, y: p.y, w: 0, h: 0, x0: p.x, y0: p.y };
    this.canvasTarget.setPointerCapture(e.pointerId);
  }

  move(e) {
    const p = this.at(e);
    const clamp = (v, max = 1, min = 0) => Math.min(max, Math.max(min, v));
    if (this.sizing) {
      const m = this.selected, s = this.sizing;
      m.w = clamp(s.w + p.x - s.p.x, 1 - m.x, 0.01);
      m.h = clamp(s.h + p.y - s.p.y, 1 - m.y, 0.01);
      return this.draw();
    }
    if (!this.draft) return;
    const x1 = clamp(p.x), y1 = clamp(p.y);
    this.draft.x = Math.min(this.draft.x0, x1);
    this.draft.y = Math.min(this.draft.y0, y1);
    this.draft.w = Math.abs(x1 - this.draft.x0);
    this.draft.h = Math.abs(y1 - this.draft.y0);
    this.draw();
  }

  up(e) {
    if (this.sizing) { this.sizing = null; return this.keep(this.selected); }
    if (!this.draft) return;
    this.canvasTarget.releasePointerCapture?.(e.pointerId);
    const c = this.canvasTarget;
    if (this.draft.w * c.width < 4 || this.draft.h * c.height < 4) { this.draft = null; this.draw(); return; }
    this.formTarget.hidden = false;
    this.labelTarget.focus();
  }

  // ---- keys ----
  key(e) {
    if (e.target.tagName === 'INPUT' && e.key !== 'Enter' && e.key !== 'Escape') return;
    if (e.key === 'Escape') return this.cancel();
    if ((e.key === 'Delete' || e.key === 'Backspace') && this.selected && this.formTarget.hidden) return this.remove(this.selected);
    if (e.key.startsWith('Arrow') && this.selected && e.target === this.canvasTarget) return this.nudge(e);
    const n = parseInt(e.key, 10);
    if (n >= 1 && n <= this.labelsValue.length) {
      this.labelTarget.value = this.labelsValue[n - 1];
      if (this.selected && this.formTarget.hidden) this.relabel(this.selected, this.labelTarget.value);
      if (this.draft && !this.readValue) { e.preventDefault(); this.save(e); }
    }
  }

  // An arrow moves the selected box 1% of the frame (5% with shift), never
  // out of it. Only from the focused canvas — elsewhere arrows scroll.
  nudge(e) {
    e.preventDefault();
    const m = this.selected, step = e.shiftKey ? 0.05 : 0.01;
    const dx = { ArrowLeft: -step, ArrowRight: step }[e.key] || 0;
    const dy = { ArrowUp: -step, ArrowDown: step }[e.key] || 0;
    m.x = Math.min(1 - m.w, Math.max(0, m.x + dx));
    m.y = Math.min(1 - m.h, Math.max(0, m.y + dy));
    this.draw();
    this.keep(m);
  }

  // One PATCH per box once the hand stops, not one per keypress.
  keep(m) {
    clearTimeout(m.keeping);
    m.keeping = setTimeout(() => {
      const { x, y, w, h } = m;
      this.send('PATCH', `${this.urlValue}/${m.id}`, { mark: { x, y, w, h } });
    }, 250);
  }

  // ---- marks ----
  async save(e) {
    e.preventDefault();
    if (!this.draft) return;
    const { x, y, w, h } = this.draft;
    const mark = { x, y, w, h, label: this.labelTarget.value, value: this.valueTarget.value || null };
    const saved = await this.send('POST', this.urlValue, { mark });
    if (saved) { this.marks.push(saved); this.selected = saved; }
    this.cancel();
  }

  cancel() {
    this.draft = null;
    this.formTarget.hidden = true;
    this.valueTarget.value = '';
    this.draw();
  }

  async relabel(mark, label) {
    const saved = await this.send('PATCH', `${this.urlValue}/${mark.id}`, { mark: { label } });
    if (saved) Object.assign(mark, saved);
    this.draw();
  }

  async remove(mark) {
    await this.send('DELETE', `${this.urlValue}/${mark.id}`);
    this.marks = this.marks.filter((m) => m !== mark);
    this.selected = null;
    this.draw();
  }

  async send(method, url, body) {
    const res = await fetch(url, {
      method, headers: { 'Content-Type': 'application/json', Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.content },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.status === 204) return null;
    const json = await res.json();
    if (!res.ok) { alert(json.error || res.statusText); return null; }
    return json;
  }

  // ---- drawing ----
  // Canvas fillStyle does not take daisy's oklch tokens. Resolve through a
  // probe so we paint the theme's rgb, not a hardcoded hex.
  token(name) {
    const el = document.createElement('span');
    el.style.color = `var(${name})`;
    this.element.appendChild(el);
    const color = getComputedStyle(el).color;
    el.remove();
    return color;
  }

  draw() {
    const c = this.canvasTarget, ctx = c.getContext('2d');
    const success = this.token('--color-success');
    const warning = this.token('--color-warning');
    const info = this.token('--color-info');
    const onSuccess = this.token('--color-success-content');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.lineWidth = 2;
    ctx.font = '12px ui-monospace, monospace';
    for (const m of this.marks) this.box(ctx, m, m === this.selected ? warning : success, onSuccess, `${m.label}${m.value ? ' = ' + m.value : ''}`);
    if (this.draft) this.box(ctx, this.draft, info, onSuccess, '');
    this.list();
  }

  box(ctx, m, color, ink, text) {
    const c = this.canvasTarget;
    const x = m.x * c.width, y = m.y * c.height, w = m.w * c.width, h = m.h * c.height;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.13;
    ctx.fillRect(x, y, w, h);
    ctx.globalAlpha = 1;
    ctx.strokeRect(x, y, w, h);
    if (!text) return;
    const tw = ctx.measureText(text).width + 8;
    ctx.fillStyle = color;
    ctx.fillRect(x, Math.max(0, y - 16), tw, 16);
    ctx.fillStyle = ink;
    ctx.fillText(text, x + 4, Math.max(12, y - 4));
  }

  list() {
    this.countTarget.textContent = this.marks.length;
    this.listTarget.innerHTML = '';
    for (const m of this.marks) {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.className = m === this.selected ? 'menu-active font-mono' : 'font-mono';
      a.textContent = `${m.label}${m.value ? ' = ' + m.value : ''}`;
      a.onclick = () => { this.selected = m; this.draw(); this.canvasTarget.focus({ preventScroll: true }); };
      const del = document.createElement('button');
      del.className = 'btn btn-ghost btn-xs ml-auto text-current';
      del.textContent = '×';
      del.setAttribute('aria-label', this.removeValue || '×');
      del.onclick = (e) => { e.stopPropagation(); this.remove(m); };
      a.appendChild(del);
      li.appendChild(a);
      this.listTarget.appendChild(li);
    }
  }
}
